const _ = require("lodash");

const {FLAGS} = require("./GlobalFlags");

const CELESTIAL_COLORS = Object.freeze({
	moon: "#14de79",
	comet: "#ffe417",
	planet: "#ffae17",
	galaxy: "#ff6017",
	shadow: "#202048",
});

const translateToDiagramColor = color => {
	const diagramColors = getDiagramColors();
	const diagramColor = diagramColors[color];
	return diagramColor || color;
};

// Get a table of colors that should be made available to CSS, which has these
// variable names suffixed with "Color". For example, if this returns the key
// value pair `foo: "#ba2"`, then in CSS, var(--fooColor) will be replaced with
// #ba2. The machinery for this is in build/css.js.
const getSharedColors = function() {
	// Theme colors appear as the series of keys to access them with intermediate keys capitalized.
	// grade2: {
	//   main: "#FA7E37",
	//   blockMain: "#ffa947",
	// }
	//  => vars grade2Main and grade2BlockMain
	// Arrays get numbered 1-n, so
	// grade2: {
	//   profile: ["#FA7E37", "#fb975e", "#fcb187", "#fdcbaf"],
	// }
	//  =>  vars grade2Profile1, grade2Profile2, grade2Profile3, and grade2Profile4
	const themeColors = {};
	_.forOwn(themeColorTable, (colors, theme) => {
		_.forOwn(colors, (color, name) => {
			if (_.isArray(color)) {
				_.forEach(color, (c, i) => {
					themeColors[theme + _.upperFirst(name) + (i + 1)] = c;
				});
			} else {
				themeColors[theme + _.upperFirst(name)] = color;
			}
		});
	});
	const schemeColors = getCurrentScheme();
	const libraryColors = getLibraryColors();
	const allKeys = _.concat(
		_.keys(themeColors),
		_.keys(schemeColors),
		_.keys(libraryColors)
	);
	const keyOverlap = _.keys(_.pickBy(_.groupBy(allKeys), k => k.length > 1));

	if (process.env.NODE_ENV !== "production" && keyOverlap.length) {
		// If getCurrentScheme includes any keys matching one created by the above
		// theme color conversion, it will overwrite that color.
		throw new Error(
			"E_OVERLAPPING_COLOR_KEYS: Theme colors and scheme colors " +
				`have overlapping keys: ${keyOverlap.join(
					", "
				)}. See shared/ColorUtil.js.`
		);
	}
	return _.extend(themeColors, schemeColors, libraryColors);
};

//Get the set of all shared colors for use in Banvas and React views
// TODO: This needs some reorganization
const getCurrentScheme = function() {
	// Add more details on how to pick colors here, return correct color scheme.
	return {
		// Collapsed Library + Theater page palette. Three cream tiers used for
		// the page background, header/tab-row, and inactive tab respectively.
		cream1: "#f5f1e6",
		cream2: "#eae6dc",
		cream3: "#d1ccc0",
		// Dark text for cream surfaces. textOnCream matches playingCardsInkDarker.
		textOnCream: "#2c2b2f",
		textOnCreamActive: "#164236",
		textOnCreamInactive: "#514835",

		//Umbrella page colors
		mainBlue: "#1b365d",
		mainAccent: "#c5e86c",
		aopsGreen: "#82b44a",
		onlineBlue: "#44c2cc",
		mainLightBlue: "#54769e",
		mainGreen: "#61ad00",
		lightGreen: "#FBFFEC",
		lightRed: "#FFEBEB",
		baRed: "#d71f26",
		baCalloutLight: "#486186",
		baCalloutDark: "#1b365d",
		lightBlue: "#EEF5FB",

		//Playing Cards colors, on /cards page
		playingCardsLighterBlue: "#EBF7FE",
		playingCardsLightBlue: "#CEEDFF",
		playingCardsBlue: "#74BEE7",
		playingCardsDarkBlue: "#2571B1",
		playingCardsDarkerBlue: "#1A3D5B",

		playingCardsLightPink: "#FEDAE2",
		playingCardsPink: "#F79FBD",
		playingCardsDarkPink: "#DF699A",
		playingCardsDarkerPink: "#B5446F",

		playingCardsLightPurple: "#F2E7F5",
		playingCardsPurple: "#CCADCD",
		playingCardsDarkPurple: "#744B77",
		playingCardsDarkerPurple: "#402942",

		playingCardsLighterRed: "#FAEEEE",
		playingCardsLightRed: "#F8D8D8",
		playingCardsRed: "#E66857",
		playingCardsDarkRed: "#CB4C3B",
		playingCardsDarkerRed: "#9D270C",

		playingCardsLighterYellow: "#FFFAEE",
		playingCardsLightYellow: "#FFF0CB",
		playingCardsYellow: "#FFD681",
		playingCardsDarkYellow: "#FFBE3F",
		playingCardsDarkerYellow: "#FAA80A",

		playingCardsInkLighter: "#BEBBB4",
		playingCardsInkLight: "#9D9B96",
		playingCardsInk: "#777674",
		playingCardsInkDark: "#525152",
		playingCardsInkDarker: "#2C2B2F",

		playingCardsPaperLighter: "#FDFCFC",
		playingCardsPaperLight: "#F5F4F3",
		playingCardsPaper: "#EBE9E5",
		playingCardsPaperDark: "#E5E3DC",
		playingCardsPaperDarker: "#DEDAD1",

		playingCardsLightGreen: "#F2F7E1",
		playingCardsGreen: "#C3D870",
		playingCardsDarkGreen: "#6B9B3B",
		playingCardsDarkerGreen: "#3F6D37",

		playingCardsWhite: "#FFFFFF",

		//The main BA color scheme (used primarily for office pages)
		red: "#ED1940",
		redHover: "#BD1433",
		blue: "#0058a8",
		blueLight: "#1968b0",
		blueHover: "#139ada",
		yellow: "#FFC640",
		yellowHover: "#e5b239",
		yellowText: "#fcbd38",
		lightYellow: "#FFFF99",
		cyan: "#00B2DD",
		cyanHover: "#27BBDE",
		cyanDarkHover: "#008EB0",
		cyanTransparent: "rgba(0, 178, 221, 0.2)",
		textBlack: "#606060",
		darkBlack: "#000000",
		gray: "#606060",
		white: "#FFFFFF",
		transparent: "rgba(0,0,0,0)",
		link: "#376fc0", // matches AoPS
		linkHover: "#00B2DD",

		//Other office page colors:
		almostWhite: "#F5F5F5", //Fallback color for white argyle
		inputBorder: "#CCCCCC",
		inputBorderHover: "#AAAAAA",
		tableBorder: "rgba(0,0,0,0.1)",
		transparentLightGray: "rgba(0,0,0,0.05)", //For use over white argyle
		officeShadow: "rgba(0,0,0,0.5)",
		officeTopBarShadow: "rgba(0,0,0,0.2)",
		offWhiteBackground: "#EEEEEE",
		disabledIcon: "rgba(0,0,0,0.2)",
		disabledLightIcon: "#CDCDCD",
		lightGrayText: "rgba(0,0,0,0.3)",
		lightGrayBorder: "rgba(0,0,0,0.4)",
		grayOnWhiteFill: "#DEDEDE",
		tableRowLight: "#F2F2F2",

		//Circle sequence buttons
		circleWhiteLine: "rgba(255,255,255,0.1)",
		circleWhiteBorder: "rgba(255,255,255,0.3)",
		circleWhiteGlow: "rgba(255,255,255,0.1)",
		circleYellowGlow: "rgba(255,198,65,0.3)", //RGB from yellow with 0.3 transparency
		circleGrayLine: "rgba(100,100,100,0.1)",
		circleGrayBorder: "#BBBBBB",
		circleGrayGlow: "rgba(0,0,0,0.1)",
		circleCyanGlow: "rgba(191, 236, 247, 0.3)", //RGB from cyan with 0.3 transparency
		circleInactive: "#CCCCCC",

		//React View only colors
		copyright: "rgba(0,0,0,0.5)",
		messageModalBlueText: "#0659A6",
		studentBlueText: "#0659A6",
		gradeModalUnfinished: "rgba(0,0,0,0.4)",
		wonkyButtonBorderFocus: "#4ebde3", //wonky button keyboard-focus edge
		blueButtonBase: "#0659A6", //in css
		blueButtonBorder: "#4191CE", //in css
		blueButtonDisabledBase: "#6E6F72", //in css
		blueButtonDisabledBorder: "#A8AAAD", //in css
		blueButtonBaseHover: "#086bc7",
		blueButtonBorderHover: "#489dd6",
		blueButtonOutline: "#ececec",
		blueButtonOutlineHover: "#d6d6d6",
		blueButtonDisabledOutline: "#ececec",
		blueButtonDisabledOutlineHover: "#e3effb",
		blueButtonActionOutline: "#247BAF",
		redButtonBase: "#ED1940",
		redButtonBorder: "#FF6B88",
		redButtonBaseHover: "#F7284E",
		redButtonBorderHover: "#FF7A96",
		dropdownBackground: "#f1d698",
		topBarBlue: "#0057A7",
		topBarBlueTransparent: "rgba(0,87,167,0.5)",
		setProgressNumber: "rgba(0,0,0,0.3)",
		setProgressSelectedNumber: "rgba(0,0,0,0.4)",
		studentLink: "#3C88CA",
		studentLinkHover: "#0659a6",
		homeSkyBlue: "#A2E5FF",

		bugReportModalBg: "#FEF9E7",
		bugReportOptionHover: "#3C88CA",
		bugReportOptionSelected: "#E8003A",

		tabButtonBaseActive: "#16ACF3",
		tabButtonBorderActive: "#FEF2C3",

		//Report table colors
		grayOverWhite: "rgba(0,0,0,0.07)",
		lightGrayOverWhite: "rgba(0,0,0,0.03)",

		//ProfilePage
		progressRowDivider: "rgba(0,0,0,0.1)",
		progressRowHover: "#F5F5F5",
		activityDay: "#FEF1B5",
		activityDayTitle: "#1A58AA",
		activityDayDark: "#FDE477",
		activityDaySelected: "#16ACF3",
		activityDayHover: "#99cfe8",
		activityDayFuture: "#E6E6E6",
		activityDayCurrent: "#FBC71E",
		activityDayFutureDark: "#CCCCCC",

		activityDayOffice: "#acd2f6",
		activityDayTitleOffice: "#1A58AA",
		activityDayDarkOffice: "#87b6e3",
		activityDaySelectedOffice: "#FFC640",
		activityDayHoverOffice: "#FDE477",
		activityDayFutureOffice: "#EEEEEE",
		activityDayFutureDarkOffice: "#BCBCBC",
		activityDayCurrentOffice: "#54acff",
		activityDayTodayOffice: "rgba(38,89,167,0.3)",
		activityDayTodayHoverOffice: "rgba(142,133,34,0.3)",

		//Beast builder colors
		bbBeigeDark: "#dccca8",
		bbBeige: "#ffdea2",
		bbPartSelected: "#15b01a",
		bbBeigeLight: "#f0ebd6",
		beastItemBase: "#ebe6d1",
		beastItemBorder: "#cec9b7",
		beastItemBaseSelected: "#EDF8E7",
		beastItemBorderSelected: "#61c000",
		beastItemBaseLocked: "#F0F0F0",
		beastItemBorderLocked: "#B3B3B3",
		beastItemAffordable: "#15B01A",
		beastItemInaffordable: "#E50000",
		transparentWhite: "rgba(255,255,255,0.5)",

		//Lock icon
		lockIcon: "#b2b2b2",
		lockIconHover: "#777777",
		lockIconLocked: "#00B2DD",
		lockIconLockedHover: "#0091b5",

		//Level Progress bar colors
		levelMain: "#D39519", //main fill
		levelHighlight: "#F8D754", //highlight below
		levelShadow: "#B8883F", //top shadow inset thing
		levelProgressHighlight: "#E8345A",
		levelProgressMain: "#E50032",

		starProgressFill: "#F7FF5F",
		starBarHighlight: "#F8D754",
		starBarShadow: "#B8883F",
		starBarMain: "#D39519",
		//TODO: The above three match the level colors. Maybe we should just use the same variables.
		starProgressNumber: "rgba(117, 75, 2, 0.9)",

		//Partial progress
		partialProgressFill: "#FFCE41",
		partialProgressFillOffice: "#75B5F0",
		partialProgressPreviousFill: "#FFDD8D",
		grayProgressBackground: "#6D6E71",

		// Chapter path
		pathIconYellow: "#f3e8ab",
		pathLightYellow: "#f6efc5",
		pathRoomGray: "#b1b3b5",
		pathRoomGrayDark: "#898b8d",

		//Banvas/shared colors:
		//Default colors
		defaultText: "#252525",
		defaultBlackFill: "#252525",
		defaultWhiteFill: "#FFFFFF",
		defaultFill: "#FFFFFF",
		defaultShadow: "rgba(50,50,50,0.5)",
		defaultInsetShadow: "rgba(0,0,0,0.5)",
		defaultDarkShadow: "rgba(0,0,0,0.4)", //Matches blue button shadows
		grayText: "#666", //Used on profile, summary pages
		lighterGrayText: "#8C8C8C",
		lighterGrayFill: "#DDDDDD",
		disabledGrayFill: "#CCC",
		lightGrayFill: "#AAAAAA",
		mediumLightGrayFill: "#bbbbbb",
		scrollbarFill: "#CCC",
		scrollbarLineFill: "rgba(224,224,224,1)",
		darkGrayFill: "#444444", //Commonly used as inactive gray
		shadedFill: "#DDDDDD", //Used for shaded input boxes
		starYellow: "#FABB50",
		iconYellow: "#FFCE41",
		iconYellowHover: "#FFD844",

		//Correct:
		correctFill: "#15B01A",
		correctText: "#15B01A",
		correctFillLight: "#AAE3AC",
		//Incorrect:
		incorrectFill: "#E50000",
		incorrectText: "#E50000",
		incorrectFillLight: "#F4BFBF",
		//Skipped:
		skippedFill: "#F86C1D",
		//Unfinalized:
		unfinalizedFill: "#FFC640",
		//Student input:
		// rectOutline: "#D0D0D0",
		strokeRectOutline: "#060606",
		rectOutline: "rgba(0,0,0,0.18)",
		inputFill: "#233E9B",
		inputText: "#233E9B",
		inputTextSelection: "#BBD3FD",
		inputFillLight: "#9fAEE1",
		inputFillVeryLight: "#D8DFF3",
		inputContrastFill: "#38A8D2", //Used to contrast with inputFill for hover effects
		inputContrastSecondaryFill: "#A8D238", //Used to go with inputContrastFill for lighter engine colors

		//Diagram Colors
		diagramRed: "#ff6673",
		diagramOrange: "#ffb366",
		diagramYellow: "#fff68c",
		diagramGreen: "#a3d96d",
		diagramTeal: "#66c9a2",
		diagramBlue: "#52a0cc",
		diagramRoyalblue: "#5c67e6",
		diagramPurple: "#9e5ce6",
		diagramPink: "#e65cae",

		//For specific engines:

		//Akari:
		akariOverlapFill: "#e97e7e",
		akariLitFill: "#C1B7DA",
		akariUnlitFill: "#6A62A8",
		akariHover: "#453F73",
		akariHighlight: "#FF8C00",
		//Grid lines for Abstract Art and Fraction Fill puzzles:
		abstractArtGridLine: "#294758",
		//Grid lines for Fillomino puzzles:
		fillominoGridLine: "#152c71",
		//Logic Puzzle
		transparentBlack: "rgba(0,0,0,0.5)",
		//Time input:
		digitalClockBackground: "#233E9B",
		digitalClockTrim: "#38A8D2", //"#527B94",
		//Clock Input:
		clockHighlight: "#5F98AC",
		//Crossed Wires:
		wiresSwitchplateBackground: "#34383D",
		wiresSwitchplate: "#677378",
		//Crosstiles:
		crosstilesShade: "#CCCCCC",
		//Arrangeable Squares, Polyominoes, Bridge Building
		shape: "#0809DF",
		shapeHover: "rgba(0,132,255,0.3)",
		shapeHoverInvalid: "rgba(255,0,0,0.2)",
		//Fill Drill:
		waterFishbowlGradientStart: "#54A6C9",
		waterFishbowlGradientEnd: "#73CCEB",
		waterFishbowlLine: "#2B84B8",
		waterFishbowlFill: "rgba(38,149,231,0.54)",
		//Multiplication Chart & Number Table:
		numberChartHeader: "rgba(35,62,155,0.1)",
		fracTurnOverline: "rgba(0, 0, 0, 0.2)",
		//Toothpicks:
		toothpick: "#0084FF", //For selected toothpicks
		toothpickLight: "#B3DAFF", //A lighter version of the above color
		toothpickRemoved: "#CCCCCC",
		// Block removal light purple
		gridLightPurple: "#D8C0F2",
		//Grid Engines:
		gridLine: "#999999",
		deletionX: "#BB7777", //TODO: Doesn't look that great
		gridReaction: "#A11761",
		gridPath: FLAGS.cleanMode ? "#233E9B" : "#38A8D2",
		//Hundreds Chart:
		hundredsChart1: "rgba(0, 204, 204, 0.25)",
		hundredsChart2: "rgba(204, 0, 204, 0.25)",
		hundredsChartNoDot: "rgba(21, 176, 26, 0.25)",
		hundredsChartIncorrect: "rgba(229,0,0, 0.45)",
		//RBT:
		//TODO: Are these all necessary and still being used? Why are there so many duplicates?
		rbtTarget: "#15B01A",
		rbtDefault: "#0809DF",
		rbtGridlines: "#F1F1F1",
		rbtHover: "rgba(0,255,255,0.5)",
		rbtHoverStep: "#FFFF77",
		rbtHoverUndo: "#FFFF77",
		rbtSolutionArrow: "#FF8C00",
		rbtStart: "#FFC125",
		rbtLightGray: "#F2F2F2",
		rbtDarkGray: "#AAAAAA",
		rbtShadow: "#949494",

		//Tenframe Toggle
		tenframeBackground: "#F2F2F2",
		tenframeOutlineOuter: "#BBBBBB",
		tenframeOutlineInner: "#999999",
		tenframeRowDivider: "#BBBBBB",
		tenframeTokenShadow: "#BBBBBB",
		tenframeTokenFadedOrange: "#FFE5C4",
		tenframeTokenFadedYellow: "#F5EDB5",
		tenframeTokenFadedTeal: "#C7EDDF",
		tenframeTokenFadedPurple: "#E6DCF0",

		//Tenframe Draggable
		tenframeTokenHighlight: "#DEB028",

		// Sum Strips Equation View
		equationViewBackground: "#e3e3e3",

		//Symmetry Defense:
		// Purple
		symmetryLine: "#8938E0",
		// Dark purple
		symmetryPenny: "#4b1584",
		// Light purple contrast when hovering over penny
		symmetryPennyContrast: "#8938E0",
		// Grey
		symmetryEmpty: "#626567",

		// Interactive Venn Diagram:
		vennHoverBlue: "#B3DAFF",
		vennLayer1: "#ECECEC",
		vennLayer2: "#D0D0D0",
		vennLayer3: "#A9A9A9",
		vennLayer4: "#7E7E7E",

		//HiddenMessage:
		hiddenMessageReactionRed: "#e97e7e",

		//Classifying quadrilaterals
		pointGray: "#808080",

		//GumballBots:
		botMult: "#ed5d62",
		botSub: "#f7941e",
		botDiv: "#3a6db5",
		botAdd: "#009444",

		//Kangarooster:
		waveBlue: "#009DB5",

		fillominoTextFill: "#000000",
		fillominoHoverColor: "#252525",

		//Digit Long Multiplication
		digitOutline: "#C0C0C0",

		//Modibot Text
		skyBlue: "#BADEEB",

		//textShapes
		shapeYellow: "#FFF799",
		shapePink: "#ee90c7",
		shapeTeal: "#8fd6ba",

		//SymSums
		gridOutline: "#4D4D4D",

		//ice blocks / ricochet robots
		rrGridMain: "#A7DFF8",
		rrGridOutline: "#5BCAF4",
		rrOuterWallLight: "#EE9C9D",
		rrOuterWallDark: "#B94242",
		rrInnerWallLight: "#3B66AF",
		rrInnerWallDark: "#2B388F",
		rrStarStroke: "#5682B0",
		rrStarFill: "#E2E669",
		rrSelectGlow: "#E2E669",
		rrPreviewFill: "#AAAAAA",
		rrPreviewStroke: "#8C8C8C",
		rrArrowFill: "#252525",

		//Demo
		demoYellow: "#FFD943",
		octagonButtonShadow: "#80411E",

		//Chapter page modal background
		chapterPageModalBackground: "#fdc959",
		genericPageModalBackground: "#ffffff",

		//Sequence
		sequenceRed: "#CC0000",

		//Leaderboards
		leaderboardsRowFill: "rgba(255, 206, 65, 0.2)",
		leaderboardsRowFillHover: "rgba(255, 206, 65, 0.3)",
		leaderboardsRowHover: "#F5F5F5",

		//Longest String
		longestStringHighlightBlue: "#05ACD3",
		longestStringRed: "#FF6673",
		longestStringRedDark: "#B93844",
		longestStringOrange: "#FFB266",
		longestStringOrangeDark: "#DD7838",
		longestStringGreen: "#A2D86D",
		longestStringGreenDark: "#539D3F",
		longestStringTeal: "#66C8A1",
		longestStringTealDark: "#0E907D",
		longestStringBlue: "#52A0CC",
		longestStringBlueDark: "#26628B",
		longestStringPurple: "#E55CAD",
		longestStringPurpleDark: "#A73B86",

		//Dots In Rectangles
		dotsInRectanglesHighlightBlue: "#06BFF9",
		dotsInRectanglesRedOutline: "#D94361",
		dotsInRectanglesRedPlate: "#FF655E",
		dotsInRectanglesPurpleOutline: "#9258E8",
		dotsInRectanglesPurplePlate: "#9F6BFF",
		dotsInRectanglesGrayOverlap: "#999999",

		//Picky Monsters
		pickyMonstersYuckyBarOutline: "#2959a1",
		pickyMonstersYuckyBarFill: "#f56272",

		//Select Grid
		selectGridSolidBlueSquareFill: "#4487F2",
		selectGridGrayBlueOutline: "#51698f",
		selectGridSelectedSquareFill: "rgba(56,168,210,0.5)",
		selectGridBGImageSelectedSquareFill: "rgba(167,182,235,0.7)",
		selectGridArrowFill: "rgba(54,54,54,0.8)",
		selectGridLightBlueOutline: "#8fcbe4",

		// Based on incorrectText (rgb 229,0,0)
		selectGridTransparentRed: "rgba(229,0,0,0.5)",
		// Based on correctText (rgb 21,176,26)
		selectGridTransparentGreen: "rgba(21,176,26,0.5)",

		// Mirror Art
		mirrorArtSelectedSquareFill: "#2ca8d0",
		mirrorArtSelectedSquareEdge: "#2897c9",
		mirrorArtMirrorLineFill: "#2e4c5b",
		mirrorArtInactiveGridLineFill: "#4d4d4d",
		mirrorArtPrefilledSquareFill: "#b3b3b3",

		// Sliding Tiles
		slidingTilesPairingFill: "#65b5ff",
		slidingTilesBevelRight: "#6a76fc",
		slidingTilesBevelLeft: "#4b57c3",
		slidingTilesBevelTop: "#7a95ff",
		slidingTilesBevelBottom: "#3940a5",
		slidingTilesInnerTray: "#647ce8",
		slidingTilesFrameFill: "#5c67e5",
		slidingTilesVideoGreen: "#009122",

		// Elevator Rides
		elevatorRidesText: "#398a8c",
		elevatorRidesTargetBackground: FLAGS.cleanMode ? "#ffc800" : "#fffed1",
		elevatorRidesLightBlue: "#d3eae5",
		elevatorRidesTargetText: FLAGS.cleanMode ? "#ff7700" : "#f19e4b",
		elevatorRidesHotelBackground: "#a4dcde",
		elevatorRidesArrow: "#697c8f",
		elevatorRidesCable: "#647d91",
		elevatorRidesButtonText: "#4b5e6d",

		// Point Connection and Point Connection Layout
		pointConnectionPostitYellow: "#fcf6a6",
		pointConnectionPostitDarkYellow: "#f5e595",
		pointConnectionPostitGray: "#546470",

		//Flycatcher
		flycatcherLandBrown: "#9f623f",
		flycatcherPondBlue: "#a5def2",
		flycatcherTonguePink: "#dd96eA",
		flycatcherCircleBlue: "#7066e5cf",
		flycatcherHighlightedYellow: "#ffffff65",
		maskWhite: "#ffffff65",

		//Greater Than Sudoku
		greaterThanSudokuHighlight: "#ffff0030",

		//Theater
		theaterPlaybackSpeedBackground: "rgba(0, 87, 167, 0.6)",
	};
};

//Gets the set of colors that differ from the normal scheme for use in secondary (non-graded) engines
const getSecondaryScheme = function() {
	return {
		inputFill: "#d69503",
		inputText: "#d69503",
		inputFillLight: "#fcbb29",
		inputContrastFill: "#efce03",
		//For secondary draw rectangle:
		shape: "#e58b03",
		//For secondary grid paths:
		gridPath: "#ffb366",
	};
};

const DRAWVAS_COLOR_OPTIONS = [
	"#FF2C3C", //red
	"#F0872B", //orange
	"#E3D129", //yellow
	"#1CBA1B", //green
	"#2486C5", //lightBlue
	"#2836E2", //darkBlue
	"#7A2AE2", //purple
	"#E22890", //pink
];

const DRAWVAS_COLOR_DEFAULT = "#2486C5"; //lightBlue from list above

const setDefaultScheme = function(context) {
	setDefaultStrokeStyle(context);
	setDefaultFillStyle(context);
};

const setDefaultStrokeStyle = function(context) {
	context.strokeStyle = "#252525";
};

const setDefaultFillStyle = function(context) {
	context.fillStyle = "#252525";
};

const getDiagramColors = function() {
	return {
		red: "#ff6673",
		orange: "#ffb366",
		yellow: "#fff68c",
		green: "#a3d96d",
		teal: "#66c9a2",
		blue: "#52a0cc",
		royalblue: "#5c67e6",
		purple: "#9e5ce6",
		pink: "#e65cae",
		shapePink: "#ee90c7",
		highlightorange: "#ff7600",
		lightOrange: "#FFCC99",
		lightGreen: "#BDDE9B",
		lightBlue: "#7AAFCC",
		lightPurple: "#B68AE6",
		lightPink: "#E68AC1",
		lightorange: "#FFCC99",
		lightgreen: "#BDDE9B",
		lightblue: "#7AAFCC",
		lightpurple: "#B68AE6",
		lightpink: "#E68AC1",
		//Lowercase versions of colors are available for their use in the canvasmath \color command
		lightgray: "#dddddd",
		lightGray: "#dddddd",
		mediumlightgray: "#bbbbbb",
		mediumLightGray: "#bbbbbb",
		mediumgray: "#999999",
		mediumGray: "#999999",
		mediumdarkgray: "#777777",
		mediumDarkGray: "#777777",
		gray: "#999999",
		darkgray: "#444444",
		darkGray: "#444444",
		white: "#FFFFFF",
		defaultblack: "#252525",
		defaultBlack: "#252525",
		black: "#000000",
		blackblack: "#000000",
		cardred: "#E50000",
		lightYellow: "#fff7a1",
		lightyellow: "#fff7a1",
		//These are for use in missing parentheses previews:
		incorrect: "#E50000",
		correct: "#15B01A",
		inputBlue: "#233E9B",
		inputblue: "#233E9B",
		// Cyan for dot trace highlighted dots
		inputContrastFill: "#38A8D2",
		inputYellow: "#d69503",
		inputyellow: "#d69503",
		// Light blue for highlighted regions in Find the Cut Line
		highlightBlue: "rgba(0,132,255,0.3)",
		// colors for calendar command
		mediumlightpinkred: "#ff4d55",
		clightred: "#ff8080",
		cpinkred: "#b93844",
		philippinesilver: "#b3b3b3",
		darkcharcoal: "#333333",
		cverylightgray: "#d9d9d9",
		// faded and dark versions of some colors (originally added for tenframes) - see T59388
		fadedRed: "#ffb3b0",
		darkRed: "#ba3844",
		fadedOrange: "#ffd6a1",
		darkOrange: "#de7838",
		fadedYellow: "#fff9b5",
		darkYellow: "#f2c41c",
		fadedGreen: "#d0f2ac",
		darkGreen: "#539e3f",
		fadedTeal: "#a7e8ce",
		darkTeal: "#0e917d",
		fadedBlue: "#95d3f5",
		darkBlue: "#26628c",
		fadedRoyalblue: "#a7bafa",
		darkRoyalblue: "#3940a6",
		fadedPurple: "#dabdff",
		darkPurple: "#6f37c4",
		fadedPink: "#fcc7e4",
		darkPink: "#a83b87",
		// same as above but lowercase
		fadedred: "#ffb3b0",
		darkred: "#ba3844",
		fadedorange: "#ffd6a1",
		darkorange: "#de7838",
		fadedyellow: "#fff9b5",
		darkyellow: "#f2c41c",
		fadedgreen: "#d0f2ac",
		darkgreen: "#539e3f",
		fadedteal: "#a7e8ce",
		darkteal: "#0e917d",
		fadedblue: "#95d3f5",
		darkblue: "#26628c",
		fadedroyalblue: "#a7bafa",
		darkroyalblue: "#3940a6",
		fadedpurple: "#dabdff",
		darkpurple: "#6f37c4",
		fadedpink: "#fcc7e4",
		darkpink: "#a83b87",
		// also added for tenframes (as background)
		veryLightGray: "#f2f2f2",
		verylightgray: "#f2f2f2",

		// video colors
		vred: "#e10000",
		vblue: "#09009c",
		vgreen: "#009122",
		vorange: "#ff7700",
		vpurple: "#8100a6",
		vpink: "#db398f",
		vlightred: "#e15858",
		vlightblue: "#0087b8",
		vlightgreen: "#00a669",
		vyellow: "#ffc800",

		// colorblind friendly palette
		cfyellow: "#ffef3a",
		cforange: "#f3823b",
		cfpink: "#dc4394",
		cfblue: "#57a8d6",
		cfpurple: "#9e5ce6",
	};
};

// TODO: the gradeN.profile colors are made up by Palmer. A designer should
// look at them.
const themeColorTable = {
	default: {
		// argyleColors: ["#FCE5B0", "#FCE9BA", "#FDF0C0", "#FEF8E7"],
		tray: "#dccca8",
		modalBackground: "#5891ca",
	},
	// profile colors HSV (s, v): (70, 50), (60, 60), (40, 70), (20, 80)
	// Some of these colors also appear in assets like the grade main
	// colors in icon assets.

	// Demo pages
	demo: {
		tray: "#077b7b",
	},

	//Grade colors for chapter page, test, and sell pages
	grade1: {
		main: "#f52338",
		light: "#f9c1c1",
		profile: ["#f52338", "#ff6661", "#ff9492", "#ffc2c2"],
		tray: "#f52338",
		blockMain: "#ff5454",
		blockBorder: "#ff8682",
		blockHover: "#ff6661",
		sequenceHover: "#ff47474D",
		modalBackground: "#ff6661",
		roomWall: "#f0293d",
		roomWallLocked: "#b81f3b",
	},

	grade2: {
		// HSV,
		main: "#FA7E37",
		light: "#f9cdc1",
		profile: ["#FA7E37", "#fb975e", "#fcb187", "#fdcbaf"],
		tray: "#e56627",
		blockMain: "#ffa947",
		blockBorder: "#FFDAB9",
		blockHover: "#ffb957",
		sequenceHover: "#ffa9474D",
		modalBackground: "#dfb753",
		roomWall: "#f48242",
		roomWallLocked: "#bd5a33",
	},
	grade3: {
		// HSV, h: 110
		main: "#38942C",
		light: "#c3debf",
		profile: ["#267F26", "#3D993D", "#6BB26B", "#A3CCA3"],
		tray: "#2D7C24",
		blockMain: "#52C74A",
		blockBorder: "#C4EBBC",
		blockHover: "#5ad950",
		sequenceHover: "#52C74A4D",
		modalBackground: "#62ba5d",
		roomWall: "#159848",
		roomWallLocked: "#11784a",
	},
	grade4: {
		// HSV, h: 185
		main: "#3299CC",
		light: "#c1e0ef",
		profile: ["#3299CC", "#5AADD6", "#84C1E0", "#ADD6EA"],
		tray: "#2B82AE",
		blockMain: "#61C1F2",
		blockBorder: "#C6E8FA",
		blockHover: "#66cbff",
		sequenceHover: "#61C1F24D",
		modalBackground: "#5891ca",
		roomWall: "#3090c1",
		roomWallLocked: "#226694",
	},
	grade5: {
		// HSV, h: 290
		main: "#84318C",
		light: "#cdacd1",
		profile: ["#84318C", "#9c5aa3", "#b583ba", "#c198c5"],
		tray: "#7B3680",
		blockMain: "#CB6ADE",
		blockBorder: "#DFB6E8",
		blockHover: "#e176f7",
		sequenceHover: "#84318C38",
		modalBackground: "#c087bb",
		roomWall: "#a85ba3",
		roomWallLocked: "#874485",
	},
	//Character themes available for blocks:
	kraken: {
		tray: "#3877b4",
		keyboard: "#C6BD9D",
		keyboardHover: "#b2aa8d",
		accent: "#C1592B",
		pen: "#C1592B",
		instructions: "#1B528D",
		modalBackground: "#3a78b5",
	},
	q: {
		tray: "#d9a400",
		keyboard: "#a36415",
		keyboardHover: "#825010",
		accent: "#EFB41D",
		pen: "#72B1C5",
		instructions: "#F4833C",
		modalBackground: "#dfb753",
	},
	fiona: {
		tray: "#519c33",
		keyboard: "#bf4e3d",
		keyboardHover: "#983e30",
		accent: "#7D0053",
		pen: "#7D0053",
		instructions: "#7D0052",
		modalBackground: "#62ba5d",
	},
	grok: {
		tray: "#ad64a7",
		keyboard: "#4a495e",
		keyboardHover: "#424154",
		accent: "#00AEB9",
		pen: "#00AEB9",
		instructions: "#8A4986",
		modalBackground: "#c087bb",
	},
	ms_levans: {
		tray: "#a27deb",
		keyboard: "#A47F5E",
		keyboardHover: "#836449",
		accent: "#7D0053",
		pen: "#7D0053",
		instructions: "#ed8332",
		modalBackground: "#ed8332",
	},
	rote: {
		tray: "#BDA06A",
		keyboard: "#548E39",
		keyboardHover: "#43712d",
		accent: "#24A6D4",
		pen: "#24A6D4",
		instructions: "#A18857",
		modalBackground: "#bfa974",
	},
	test: {
		keyboard: "#BAA175",
		keyboardHover: "#94805D",
		accent: "#2D8DBB",
		pen: "#AF1A41",
		modalBackground: "#69b95b",
	},
	challenge: {
		tray: "#000000",
		keyboard: "#323333",
		keyboardHover: "#5a5b5b",
		accent: "#070000",
		pen: "#EE4036",
		modalBackground: "#474747",
	},
	review: {
		tray: "#D78F3B",
		keyboard: "#F2A44B",
		keyboardHover: "#d99343",
		accent: "#4687C3",
		pen: "#4687C3",
		modalBackground: "#e1a75b",
	},
	lab: {
		main: "#8c61d9",
		light: "#a989e3",
		profile: ["#9d78ad", "#9168a3", "#a988b7", "#c0a8ca"],
		tray: "#57548a",
		trayHighlight: "#000",
		// tray: "#8c61d9",
		// trayHighlight: "#a989e3",
		blockMain: "#8c61d9",
		blockBorder: "#a989e3",
		blockHover: "#9a75de",
		modalBackground: "#AF82BD",
		instructions: "#8c61d9",
		accent: "#6949a3",
		keyboard: "#6E89AD",
		keyboardHover: "#576B8D",
		consoleText: "#65dc7f",
		...CELESTIAL_COLORS,
	},
	headmaster: {
		accent: "#2D8DBB",
		instructions: "#2D8DBB",
	},
	clean: {
		tray: "#66666688",
		keyboard: "#323333",
		keyboardHover: "#5a5b5b",
		accent: "#070000",
		pen: "#EE4036",
		modalBackground: "#474747",
	},
	//Characters themes only available for instructions (not blocks)
	alex: {
		instructions: "#00659F",
	},
	announcer: {
		instructions: "#2D8340",
	},
	clod: {
		instructions: "#242424",
	},
	rg: {
		instructions: "#A82C22",
	},
	grogg: {
		instructions: "#5B1B75",
	},
	lizzie: {
		instructions: "#7BC33B",
	},
	winnie: {
		instructions: "#D23A7E",
	},
};

const getLibraryColors = function() {
	return {
		// Book chapter menu colors
		krakenDark: "#70b4f7",
		qDark: "#fae03a",
		rgDark: "#f8c44e",
		fionaDark: "#97d05d",
		roteDark: "#AEB878",
		grokDark: "#e58ade",
		notes: "#ffffff",
		recessDark: "#ec3d3e",
		mathmeetDark: "#70b146",
		channel5Dark: "#A380FF",
		carpoolDark: "#C9A6FF",
		// same shade as book page, as after book chapter menu discontinued
		// added for some degree of backwards compatibility in case we add it back in
		headmasterDark: "#E58ADE", // same as Grok
		levansDark: "#A27DEB",
		kitchentableDark: "#877AB8",

		// Book page colors
		krakenPage: "#BDD9F4",
		qPage: "#FCF99D",
		rgPage: "#FACF74",
		grokPage: "#E6CEE5",
		fionaPage: "#CDE5B6",
		rotePage: "#AEB878",
		mathmeetPage: "#CDE5B5",
		channel5Page: "#D4CEE7",
		carpoolPage: "#D4CEE7",
		headmasterPage: "#E6CEE5", // same as Grok
		kitchentablePage: "#877AB8",
	};
};

const getThemeColorTable = function() {
	return themeColorTable;
};

const getThemeColors = function(themeName, clean = false) {
	if (clean) {
		return themeColorTable["clean"];
	}
	// If undefined/missing, default to fiona.
	// This allows us to add new characters to the database and have curriculum
	// start to use them while still having the problems they're making play correctly.
	return themeColorTable[themeName] || themeColorTable["fiona"];
};

const getMultipleColors = function(multiple) {
	if (multiple > 12) {
		multiple = 13;
	}
	const f = 0.55;
	const r = Math.round(Math.sin(f * multiple + 0) * 127 + 128);
	const g = Math.round(Math.sin(f * multiple + (2 * Math.PI) / 3) * 127 + 128);
	const b = Math.round(Math.sin(f * multiple + (4 * Math.PI) / 3) * 127 + 128);
	return "rgb(" + r + "," + g + "," + b + ")";
};

//Changed the getMultipleColors() to include less red to be used on the hundredsMultiple.js
const getMultipleColorsHundreds = function(multiple) {
	if (multiple > 12) {
		multiple = 13;
	}
	const f = 0.55;
	const r = Math.round(Math.sin(f * multiple + 0) * 127 + 32);
	const g = Math.round(Math.sin(f * multiple + (2 * Math.PI) / 3) * 127 + 128);
	const b = Math.round(Math.sin(f * multiple + (4 * Math.PI) / 3) * 127 + 128);
	return "rgb(" + r + "," + g + "," + b + ")";
};

const calculateCSSFilter = function(origHSL, hsl) {
	const oldHSL = origHSL || {};
	const newHSL = hsl || {};
	const dH = newHSL.h - oldHSL.h;
	const dL = newHSL.l / oldHSL.l;
	const dS = newHSL.s / oldHSL.s;
	return (
		"hue-rotate(" + dH + "deg) brightness(" + dL + ") saturate(" + dS + ")"
	);
};

const getGradeColor = function(grade, colorType = "main") {
	return themeColorTable["grade" + grade][colorType];
};

const getStoreColor = function(bookHash, colorType = "main") {
	const grade = bookHash.charAt(0);
	return getGradeColor(grade, colorType);
};

const hexToRGBA = function(hex, alpha) {
	let c;

	if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
		c = hex.substring(1).split("");
		if (c.length === 3) {
			c = [c[0], c[0], c[1], c[1], c[2], c[2]];
		}
		c = "0x" + c.join("");
		return (
			"rgba(" +
			[(c >> 16) & 255, (c >> 8) & 255, c & 255].join(",") +
			"," +
			alpha +
			")"
		);
	}

	return "";
};

const getBlueStarColors = function(numShades) {
	const rgbDark = [17, 93, 165];
	const rgbLight = [117, 181, 240];

	const colors = _.range(numShades - 1).map(shadeNum => {
		const pct = shadeNum / (numShades - 2);

		const rgb = _.range(3).map(i =>
			_.round((rgbLight[i] - rgbDark[i]) * pct + rgbDark[i], 0)
		);

		return "rgba(" + rgb.join(", ") + ", 1)";
	});

	colors.push("rgba(170, 182, 229, 1)");

	return colors;
};

const getPurpleCelestialColors = function(numShades) {
	if (numShades === 4) {
		// specifically curated colors
		return ["#660099", "#8613BF", "#A92EE6", "#C44DFF"];
	}
	const rgbDark = [141, 108, 155];
	const rgbLight = [198, 181, 205];

	const colors = _.range(numShades).map(shadeNum => {
		const pct = shadeNum / (numShades - 1);

		const rgb = _.range(3).map(i =>
			_.round((rgbLight[i] - rgbDark[i]) * pct + rgbDark[i], 0)
		);

		return "rgba(" + rgb.join(", ") + ", 1)";
	});

	return colors;
};

const getTextShapeDefaultColors = function() {
	return {
		square: "shapeTeal",
		circle: "shapePink",
		triangle: "shapeYellow",
	};
};

const getSharedColorsMap = function() {
	const pathToVariableMap = {};

	// Process theme colors (like grade2.profile.0 -> grade2Profile1)
	_.forOwn(themeColorTable, (colors, theme) => {
		_.forOwn(colors, (color, name) => {
			if (_.isArray(color)) {
				_.forEach(color, (c, i) => {
					const path = `${theme}.${name}.${i}`;
					const variableName = theme + _.upperFirst(name) + (i + 1);
					pathToVariableMap[path] = variableName;
				});
			} else {
				const path = `${theme}.${name}`;
				const variableName = theme + _.upperFirst(name);
				pathToVariableMap[path] = variableName;
			}
		});
	});

	// Process scheme colors (these don't have nested paths, so path = variable name)
	const schemeColors = getCurrentScheme();
	_.forOwn(schemeColors, (color, name) => {
		pathToVariableMap[name] = name;
	});

	// Process library colors (these don't have nested paths, so path = variable name)
	const libraryColors = getLibraryColors();
	_.forOwn(libraryColors, (color, name) => {
		pathToVariableMap[name] = name;
	});

	return pathToVariableMap;
};

// Fill/shadow per styleType for ConfirmModalShape (legacy confirm-*.svg palette).
const CONFIRM_MODAL_SHAPE_COLORS = Object.freeze({
	challenge: {shadow: "#242424", fill: "#474747"},
	default: {shadow: "#1B528D", fill: "#5891ca"},
	fiona: {shadow: "#1A8231", fill: "#62BA5D"},
	grade1: {shadow: "#F52338", fill: "#FF6661"},
	grade2: {shadow: "#C1832B", fill: "#dfb753"},
	grade3: {shadow: "#1A8231", fill: "#62BA5D"},
	grade4: {shadow: "#1B528D", fill: "#5891ca"},
	grade5: {shadow: "#8A4986", fill: "#c087bb"},
	grok: {shadow: "#8A4986", fill: "#c087bb"},
	kraken: {shadow: "#1B528D", fill: "#3A78B5"},
	lab: {shadow: "#7D608A", fill: "#9D78AD"},
	ms_levans: {shadow: "#ED8332", fill: "#EBAC75"},
	q: {shadow: "#C1832B", fill: "#dfb753"},
	review: {shadow: "#C4802A", fill: "#e1a75b"},
	rote: {shadow: "#A18857", fill: "#bfa974"},
	test: {shadow: "#2D8340", fill: "#69b95b"},
});

module.exports = {
	translateToDiagramColor,
	getSharedColors,
	setDefaultScheme,
	setDefaultFillStyle,
	setDefaultStrokeStyle,
	getCurrentScheme,
	getSecondaryScheme,
	getThemeColorTable,
	getThemeColors,
	getLibraryColors,
	getMultipleColors,
	getMultipleColorsHundreds,
	getDiagramColors,
	calculateCSSFilter,
	getGradeColor,
	getStoreColor,
	getBlueStarColors,
	getPurpleCelestialColors,
	hexToRGBA,
	getTextShapeDefaultColors,
	CELESTIAL_COLORS,
	DRAWVAS_COLOR_OPTIONS,
	DRAWVAS_COLOR_DEFAULT,
	getSharedColorsMap,
	CONFIRM_MODAL_SHAPE_COLORS,
};
